









































































































































































































































































































































































import { SfLoader, SfHeading } from "@storefront-ui/vue";
import {
  defineComponent,
  ref,
  Ref,
  useContext,
  useRoute,
  onMounted,
  computed,
  watch,
  useRouter,
} from "@nuxtjs/composition-api";
import { useCache, CacheTagPrefix } from "@vue-storefront/cache";
import { getMetaInfo } from "~/helpers/getMetaInfo";
import { useContent } from "~/composables";
import { useProduct } from "~/modules/catalog/product/composables/useProduct";
import { useProductsWithCommonProductCardProps } from "~/modules/catalog/category/components/views/useProductsWithCommonCardProps";
import { useFilters } from "~/modules/catalog/category/components/filters/useFilters";
import { useUiHelpers, useSearchProducts } from "~/composables";
import PriceRangeSlider from "~/modules/catalog/category/components/filters/filterPrice/PriceRangeSlider.vue";
import CategoryProductCard from "~/modules/catalog/category/components/views/CategoryProductCard.vue";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import ProductLoadItem from "~/components/SkeletonLoader/ProductLoadItem.vue";
import { getProductFilterCommand } from "~/modules/catalog/category/components/filters/command/getProductFilterCommand";
import { useClevertapStore } from "~/stores/clevertap";
import { mapAgg } from "~/modules/catalog/category/helpers/mapAgg";

import type {
  CmsPage,
  Aggregation,
  AggregationOption,
} from "~/modules/GraphQL/types";
import type {
  SelectedFiltersInterface,
  RemovableFilterInterface,
} from "~/modules/catalog/category/components/filters/useFilters";

export interface UseFiltersProviderInterface {
  selectedFilters: Ref<SelectedFiltersInterface>;
  filters: Ref<Aggregation[]>;
}

export default defineComponent({
  name: "SearchPage",
  components: {
    SfLoader,
    SfHeading,
    PriceRangeSlider,
    CategoryProductCard,
    SkeletonLoader,
    ProductLoadItem,
  },
  setup() {
    const { loadPage, loading, error } = useContent();
    const route = useRoute();
    const router = useRouter();
    const { fetchProducts, loading: productLoading } = useSearchProducts();
    const { changeSorting, changeFilters, clearFilters } = useUiHelpers();
    const clevertapStore = useClevertapStore();

    // Search state
    const page = ref<CmsPage | null>(null);
    const searchQuery = ref("");
    const products = ref<any[]>([]);
    const totalCount = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);

    // Filter state
    const filters = ref<Aggregation[]>([]);
    const removableFilters = ref<RemovableFilterInterface[]>([]);
    const isOpenSize = ref(false);
    const isOpenColor = ref(false);
    const isOpenPrice = ref(false);
    const priceFilterMin = ref<number>(0);
    const priceFilterMax = ref<number>(0);
    const {
      selectedFilters: allSelectedFilters,
      selectFilter,
      removeFilter,
      isFilterSelected,
      getRemovableFilters,
      getSelectedFiltersFromUrl,
    } = useFilters();

    // Create a filtered version of selectedFilters that only has size and price
    const selectedFilters = computed(() => {
      const filtered: SelectedFiltersInterface = {};

      // Only keep size and price filters
      const allowedFilters = ["size", "price"];

      // Copy only the allowed filter types
      for (const filterType of allowedFilters) {
        if (allSelectedFilters.value[filterType]) {
          filtered[filterType] = allSelectedFilters.value[filterType];
        }
      }

      return filtered;
    });

    // Update removable filters based on selected filters
    const updateRemovableFilters = () => {
      removableFilters.value = getRemovableFilters(
        filters.value,
        selectedFilters.value
      );
    };

    // Filter handling
    const chooseFilter = (filter: Aggregation, option: AggregationOption) => {
      // Only allow size and price filters
      if (!["size", "price"].includes(filter.attribute_code)) {
        return;
      }

      if (isFilterActive(option)) {
        removeFilter(filter.attribute_code, option.value);
      } else {
        selectFilter(filter, option);
      }

      // Apply filters and trigger product fetch
      doApplyFilters();
    };

    const changeFilterPrice = (option: AggregationOption) => {
      const filter = filters.value.find(
        (filter) => filter.attribute_code === "price"
      );
      if (filter) {
        selectFilter(filter, option);
        doApplyFilters();
      }
    };

    const isFilterActive = (option: AggregationOption) => {
      return removableFilters.value.some(
        (item: AggregationOption) => item.value === option.value
      );
    };

    const doApplyFilters = () => {
      // Update UI helpers with selected filters
      changeFilters(selectedFilters.value, false);

      // Update removable filters for UI
      updateRemovableFilters();

      // Clear the query processed cache to force a new fetch with these filters
      queryProcessed.value = new Set();

      // Fetch products with current search query and new filters
      fetchProductsSearch(searchQuery.value, false, true);
    };

    // Mobile filter functionality
    const chooseFilterMobile = (
      filter: Aggregation,
      option: AggregationOption
    ) => {
      if (isFilterActive(option)) {
        removeFilter(filter.attribute_code, option.value);
      } else {
        selectFilter(filter, option);
      }
      updateRemovableFilters();
    };

    const changeFilterPriceMobile = (option: AggregationOption) => {
      const filter = filters.value.find(
        (filter) => filter.attribute_code === "price"
      );
      if (filter) {
        selectFilter(filter, option);
        updateRemovableFilters();
      }
    };

    const selected = ref<SelectedFiltersInterface>(getSelectedFiltersFromUrl());

    const CloseFilterMobile = () => {
      // Reset mobile filters to match URL state when closing without applying
      selected.value = getSelectedFiltersFromUrl();

      // Update selected filters from URL
      Object.keys(allSelectedFilters.value).forEach((key) => {
        allSelectedFilters.value[key] = selected.value[key] || [];
      });

      updateRemovableFilters();

      // Close mobile filter panel
      const element = document.getElementById("js-filter-landing-mobile");
      if (element) element.classList.remove("active");
    };

    const doClearFiltersMobile = () => {
      // Clear all filter selections
      Object.keys(allSelectedFilters.value).forEach((key) => {
        allSelectedFilters.value[key] = [];
      });

      updateRemovableFilters();
    };

    const doApplyFiltersMobile = () => {
      // Apply filters to global state
      if (Object.keys(selectedFilters.value).length === 0) {
        clearFilters(false);
      } else {
        changeFilters(selectedFilters.value, false);
      }

      // Reset cache to force new API call
      queryProcessed.value = new Set();

      // Scroll to top for better UX
      if (process.client && window?.scroll) {
        window.scroll(0, 0);
      }

      // Force fetch with new filters
      fetchProductsSearch(searchQuery.value, false, true);

      // Close the mobile filter panel
      CloseFilterMobile();
    };

    const OpenFilterMobile = () => {
      // Store current filter state for possible reset
      if (typeof structuredClone === "function") {
        selected.value = structuredClone(getSelectedFiltersFromUrl());
      } else {
        // Fallback for browsers without structuredClone
        selected.value = JSON.parse(
          JSON.stringify(getSelectedFiltersFromUrl())
        );
      }

      // Update removable filters display
      removableFilters.value = getRemovableFilters(
        filters.value,
        selectedFilters.value
      );

      // Open the mobile filter panel
      const element = document.getElementById("js-filter-landing-mobile");
      if (element) element.classList.add("active");
    };

    // Sort handling
    const sortValue = ref("");
    const isFilterNavbarOpen = ref(false);
    const isFilterNavbarOpenMobile = ref(false);

    const changeSortOderValue = (value: string) => {
      // If clicking on the already selected sort option, remove sorting
      if (sortValue.value === value) {
        // Clear the sort value
        sortValue.value = "";

        // Update the global UI state with no sorting
        changeSorting("", false);
      } else {
        // Update the sort value with the new selection
        sortValue.value = value;

        // Update the global UI state
        changeSorting(value, false);
      }

      // Clear the query processed cache to force a new fetch
      queryProcessed.value = new Set();

      // Force products to refresh with new sort order
      fetchProductsSearch(searchQuery.value, false, true);

      // Close sort dropdowns
      setTimeout(() => {
        isFilterNavbarOpen.value = false;
        isFilterNavbarOpenMobile.value = false;
      }, 100);
    };

    const toggleFilterNavbar = () => {
      isFilterNavbarOpen.value = !isFilterNavbarOpen.value;
    };

    const offFilterNavbarMobile = () => {
      isFilterNavbarOpenMobile.value = false;
    };

    // Add tracking variables to prevent duplicate API calls
    const queryProcessed = ref(new Set<string>());
    const initialLoadComplete = ref(false);
    const filtersLoaded = ref(false);
    const initialAggregationsLoaded = ref(false);

    // New function to fetch initial aggregations using getProductFilterCommand
    const fetchInitialAggregations = async (term: string) => {
      if (!term || initialAggregationsLoaded.value) return;

      try {
        // Use getProductFilterCommand to fetch aggregations
        const params = {
          search: term.trim(),
          aggs: true,
        };
        const { aggregations: agg } = await fetchProducts(params);

        const aggregations = mapAgg(agg);
        // const aggregations = await getProductFilterCommand.execute({
        //   filter: {},
        //   search: term.trim(),
        // });
        // Process aggregations from the filter command
        if (aggregations) {
          processFiltersFromSearchResults(aggregations);
          initialAggregationsLoaded.value = true;
        }
      } catch (err) {
        console.error("Error fetching initial aggregations:", err);
      }
    };

    // Processing filters from search results or getProductFilterCommand
    const processFiltersFromSearchResults = (aggregations: any[]) => {
      if (!aggregations || !Array.isArray(aggregations)) return;

      // Only process aggregations once when component is first loaded
      if (filtersLoaded.value) return;

      // Filter and sort aggregations
      filters.value = aggregations
        .filter((filter) => ["price", "size"].includes(filter.attribute_code))
        .sort((a, b) => {
          const order = ["price", "size"];
          return (
            order.indexOf(a.attribute_code) - order.indexOf(b.attribute_code)
          );
        });

      // Extract price range from filters
      const priceFilter = filters.value.find(
        (filter) => filter.attribute_code === "price"
      );

      if (
        priceFilter &&
        priceFilter.options &&
        priceFilter.options.length > 0
      ) {
        const priceValues = priceFilter.options
          .map((option) => {
            return Number(option.value);
          })
          .filter((value) => !isNaN(value));

        if (priceValues.length > 0) {
          priceFilterMin.value = Math.min(...priceValues);
          priceFilterMax.value = Math.max(...priceValues);
        } else {
          priceFilterMin.value = -Infinity;
          priceFilterMax.value = Infinity;
        }
      } else {
        priceFilterMin.value = -Infinity;
        priceFilterMax.value = Infinity;
      }

      updateRemovableFilters();
      filtersLoaded.value = true;
    };

    const getParamFilter = () => {
      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }
      let paramFilter: any = {};

      if (routerQuery) {
        if (routerQuery.size) {
          paramFilter.sizeIds = Array.isArray(routerQuery.size)
            ? routerQuery.size
            : [routerQuery.size];
        }

        if (routerQuery.price) {
          paramFilter.priceRanger = Array.isArray(routerQuery.price)
            ? routerQuery.price
            : routerQuery.price.split("_");
        }

        if (routerQuery.mastercolor) {
          paramFilter.colorIds = Array.isArray(routerQuery.mastercolor)
            ? routerQuery.mastercolor
            : [routerQuery.mastercolor];
        }

        if (routerQuery.sort && routerQuery.sort == "created_at_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "created_at";
        }

        if (routerQuery.sort && routerQuery.sort == "price_ASC") {
          paramFilter.order = "asc";
          paramFilter.orderby = "final_price";
        }

        if (routerQuery.sort && routerQuery.sort == "price_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "final_price";
        }

        return paramFilter;
      }
    };

    // Fetch products
    const fetchProductsSearch = async (
      term: string,
      loadMore = false,
      sortChanged = false
    ) => {
      if (!term) return;

      // Handle special sort field cases that contain underscores
      let sortKey = "";
      let sortDirection = "";

      // Always parse sort if sortValue has a value
      if (sortValue.value) {
        // Special handling for sort values that already contain underscores
        if (sortValue.value.startsWith("created_at_")) {
          sortKey = "created_at";
          sortDirection = sortValue.value.replace("created_at_", "");
        } else {
          // Handle normal cases (price_ASC, price_DESC)
          const lastUnderscoreIndex = sortValue.value.lastIndexOf("_");
          if (lastUnderscoreIndex !== -1) {
            sortKey = sortValue.value.substring(0, lastUnderscoreIndex);
            sortDirection = sortValue.value.substring(lastUnderscoreIndex + 1);
          }
        }
      }

      const queryKey = `${term}-${
        loadMore ? currentPage.value : 1
      }-${sortKey}${sortDirection}`;

      if (!loadMore && !sortChanged && queryProcessed.value.has(queryKey)) {
        return;
      }

      try {
        // Track that we're processing this query
        if (!loadMore) {
          queryProcessed.value.add(queryKey);

          // Reset products when not loading more
          currentPage.value = 1;
          products.value = [];
        }

        // Initialize search parameters with just the search term (no created_at filter)
        const searchParams: {
          search?: string;
          filter?: any;
          pageSize: number;
          currentPage: number;
          sort?: { [key: string]: string };
        } = {
          search: term.trim(), // Keep the direct search parameter
          pageSize: pageSize.value,
          currentPage: currentPage.value,
          // Always include position sorting as default
          sort: {},
          // Initialize filter object
          filter: {},
        };

        // Add additional sort parameters if we have a sortDirection
        if (sortDirection) {
          searchParams.sort = {
            [sortKey]: sortDirection, // Add user-selected sort as secondary
          };
        }

        // Add additional filters from the selected filters (size, price, etc.)
        if (
          selectedFilters.value &&
          Object.keys(selectedFilters.value).length > 0
        ) {
          // Process each filter type (size, price, etc.)
          Object.entries(selectedFilters.value).forEach(
            ([filterType, filterValues]) => {
              if (filterType === "price" && filterValues) {
                // For price range, use from_to format
                // Convert to string safely regardless of type
                let priceValueString: string;

                if (Array.isArray(filterValues) && filterValues.length > 0) {
                  priceValueString = String(filterValues[0]);
                } else {
                  // Handle non-array case safely
                  priceValueString = String(filterValues);
                }

                const priceValues = priceValueString.split("_");

                if (priceValues.length === 2) {
                  searchParams.filter[filterType] = {
                    from: priceValues[0],
                    to: priceValues[1],
                  };
                }
              } else if (
                Array.isArray(filterValues) &&
                filterValues.length > 0
              ) {

                // For array values like sizes, use the "in" operator
                searchParams.filter[filterType] = {
                  in: filterValues,
                };
              }
            }
          );
        }

        const param = {
          search: searchParams.search,
          currentPage: searchParams.currentPage,
          pageSize: searchParams.pageSize,
          filters: getParamFilter(),
          aggs: true,
        };

        const { data, total, aggregations } = await fetchProducts(param);

        // This is the actual API call we want to control
        const result = data || [];

        // Process search results
        if (loadMore) {
          // Append new products
          products.value = [...products.value, ...(result || [])];
        } else {
          // Replace products
          products.value = result || [];

          // Process aggregations only if they haven't been loaded yet
          if (aggregations && !filtersLoaded.value) {
            processFiltersFromSearchResults(aggregations);
          }
        }

        totalCount.value = total || 0;
        initialLoadComplete.value = true;
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };

    // Run initialization once on mount
    onMounted(async () => {
      const query = route.value.query.q as string;
      const sortParam = route.value.query.sort as string;

      // Set the sort value from URL if available, otherwise keep the default
      if (
        sortParam &&
        ["created_at_DESC", "price_ASC", "price_DESC"].includes(sortParam)
      ) {
        sortValue.value = sortParam;
      }

      // Set search query from URL
      if (query) {
        searchQuery.value = query;

        // First fetch aggregations using getProductFilterCommand
        await fetchInitialAggregations(query);

        // Then fetch products with filters from URL
        await fetchProductsSearch(query);
      }
    });

    // Watch query parameter changes
    watch(
      () => route.value.query,
      (newQuery, oldQuery) => {
        // Check for search query changes
        if (newQuery.q !== oldQuery.q) {
          // Reset tracking for this new search term
          queryProcessed.value = new Set();
          searchQuery.value = (newQuery.q as string) || "";

          // Only fetch products if there's a search term
          if (newQuery.q) {
            fetchProductsSearch(newQuery.q as string);
          } else {
            // Clear products when search is cleared
            products.value = [];
            totalCount.value = 0;
          }
        }

        // Check for sort changes
        if (newQuery.sort !== oldQuery.sort) {
          const sortParam = newQuery.sort as string;
          if (
            sortParam &&
            ["created_at_DESC", "price_ASC", "price_DESC"].includes(sortParam)
          ) {
            sortValue.value = sortParam;
            if (searchQuery.value) {
              queryProcessed.value = new Set();
              fetchProductsSearch(searchQuery.value, false, true);
            }
          }
        }
      },
      { deep: true }
    );

    // Computed properties for selected filters
    const selectedPriceRange = computed(() => {
      return removableFilters.value.find((filter) => filter.id === "price");
    });

    const selectedSize = computed(() => {
      return removableFilters.value.filter((filter) => filter.id === "size");
    });

    // Add isLoadingMore ref to track load more operation specifically
    const isLoadingMore = ref(false);

    // Load more products
    const loadMoreProducts = async () => {
      if (isLoadingMore.value) return; // Prevent multiple simultaneous requests

      if (products.value.length < totalCount.value) {
        // Set loading more state
        isLoadingMore.value = true;

        try {
          // Increment page number before fetching more products
          currentPage.value++;

          // Initialize search parameters specifically for loading more products
          const searchParams: {
            search?: string;
            filter?: any;
            pageSize: number;
            currentPage: number;
            sort?: { [key: string]: string };
          } = {
            search: searchQuery.value.trim(),
            pageSize: pageSize.value,
            currentPage: currentPage.value,
            sort: {},
            filter: {}, // Initialize filter object
          };

          // Add sort parameters if available
          if (sortValue.value) {
            let sortKey = "";
            let sortDirection = "";

            if (sortValue.value.startsWith("created_at_")) {
              sortKey = "created_at";
              sortDirection = sortValue.value.replace("created_at_", "");
            } else {
              const lastUnderscoreIndex = sortValue.value.lastIndexOf("_");
              if (lastUnderscoreIndex !== -1) {
                sortKey = sortValue.value.substring(0, lastUnderscoreIndex);
                sortDirection = sortValue.value.substring(
                  lastUnderscoreIndex + 1
                );
              }
            }

            if (sortKey && sortDirection) {
              searchParams.sort = {
                [sortKey]: sortDirection,
              };
            }
          }

          // Add filters from selected filters
          if (
            selectedFilters.value &&
            Object.keys(selectedFilters.value).length > 0
          ) {
            Object.entries(selectedFilters.value).forEach(
              ([filterType, filterValues]) => {
                if (filterType === "price" && filterValues) {
                  const priceValues = Array.isArray(filterValues)
                    ? String(filterValues[0]).split("_")
                    : String(filterValues).split("_");

                  if (priceValues.length === 2) {
                    searchParams.filter[filterType] = {
                      from: priceValues[0],
                      to: priceValues[1],
                    };
                  }
                } else if (
                  Array.isArray(filterValues) &&
                  filterValues.length > 0
                ) {
                  searchParams.filter[filterType] = {
                    in: filterValues,
                  };
                }
              }
            );
          }

           const param = {
          search: searchParams.search,
          currentPage: searchParams.currentPage,
          pageSize: searchParams.pageSize,
          filters: getParamFilter(),
        };

        const { data, total } = await fetchProducts(param);

          // Make API call directly for more products
          // const result = await searchProducts(searchParams);
          const result = data || [];
          // Append new products to existing ones
          if (result && result.length > 0) {
            products.value = [...products.value, ...result];
          }

          // Update total count if provided in result
          if (total !== undefined) {
            totalCount.value = total;
          }
        } catch (err) {
          console.error("Error loading more products:", err);
        } finally {
          // Reset loading state
          isLoadingMore.value = false;
        }
      }
    };

    // Computed property to check if there are any active filters
    const hasActiveFilters = computed(() => {
      return removableFilters.value.length > 0;
    });

    // Track product click from search results
    const trackProductClick = (product, position) => {
      try {
        clevertapStore.setInforFromSearch({
          is_search_result: true,
          seach_result_query: searchQuery.value,
          position: position,
        });
      } catch (error) {
        console.error("Error tracking product click:", error);
      }
    };

    return {
      // Search state
      page,
      loading,
      searchQuery,
      products,
      totalCount,
      currentPage,
      pageSize,
      fetchProductsSearch,
      loadMoreProducts,

      // Filter state
      filters,
      removableFilters,
      isOpenSize,
      isOpenPrice,
      isOpenColor,
      priceFilterMin,
      priceFilterMax,
      selectedFilters,
      selectedPriceRange,
      selectedSize,
      chooseFilter,
      changeFilterPrice,
      isFilterActive,
      doApplyFilters,

      // Sorting state
      sortValue,
      isFilterNavbarOpen,
      isFilterNavbarOpenMobile,
      changeSortOderValue,
      toggleFilterNavbar,
      offFilterNavbarMobile,

      // Mobile handling
      productLoading,
      OpenFilterMobile,
      CloseFilterMobile,
      doApplyFiltersMobile,
      doClearFiltersMobile,
      changeFilterPriceMobile,
      chooseFilterMobile,

      // Other state
      initialLoadComplete,
      filtersLoaded,
      initialAggregationsLoaded,
      isLoadingMore,
      hasActiveFilters,
      trackProductClick,
    };
  },
  head() {
    return {
      title: this.searchQuery ? `Tìm kiếm: ${this.searchQuery}` : "Tìm kiếm",
      ...getMetaInfo(this.page),
    };
  },
});
